import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Search } from 'lucide-react';

const Translations = () => {
  const { API } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translations, setTranslations] = useState([]);
  const [search, setSearch] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValues, setNewValues] = useState({ sq: '', de: '', en: '' });

  useEffect(() => {
    fetchTranslations();
  }, [API]);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API}/admin/translations`, { withCredentials: true });
      setTranslations(response.data.translations || []);
    } catch (error) {
      toast.error('Failed to load translations');
    } finally {
      setLoading(false);
    }
  };

  const saveTranslation = async (language, key, value) => {
    try {
      await axios.put(`${API}/admin/translations`, { language, key, value }, { withCredentials: true });
      toast.success('Translation saved');
    } catch (error) {
      toast.error('Failed to save translation');
    }
  };

  const deleteTranslation = async (language, key) => {
    try {
      await axios.delete(`${API}/admin/translations/${language}/${key}`, { withCredentials: true });
      setTranslations(prev => prev.filter(t => !(t.language === language && t.key === key)));
      toast.success('Translation deleted');
    } catch (error) {
      toast.error('Failed to delete translation');
    }
  };

  const addNewTranslation = async () => {
    if (!newKey.trim()) {
      toast.error('Please enter a key');
      return;
    }

    setSaving(true);
    try {
      const promises = Object.entries(newValues).map(([lang, value]) => {
        if (value.trim()) {
          return axios.put(`${API}/admin/translations`, {
            language: lang,
            key: newKey.trim(),
            value: value.trim()
          }, { withCredentials: true });
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      await fetchTranslations();
      setNewKey('');
      setNewValues({ sq: '', de: '', en: '' });
      toast.success('Translation added');
    } catch (error) {
      toast.error('Failed to add translation');
    } finally {
      setSaving(false);
    }
  };

  const handleValueChange = (language, key, value) => {
    setTranslations(prev => prev.map(t => 
      t.language === language && t.key === key 
        ? { ...t, value, changed: true } 
        : t
    ));
  };

  const saveAllChanges = async () => {
    const changedTranslations = translations.filter(t => t.changed);
    if (changedTranslations.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      await Promise.all(changedTranslations.map(t => 
        saveTranslation(t.language, t.key, t.value)
      ));
      setTranslations(prev => prev.map(t => ({ ...t, changed: false })));
      toast.success(`Saved ${changedTranslations.length} translations`);
    } catch (error) {
      toast.error('Failed to save some translations');
    } finally {
      setSaving(false);
    }
  };

  // Group translations by key
  const groupedTranslations = translations.reduce((acc, t) => {
    if (!acc[t.key]) acc[t.key] = {};
    acc[t.key][t.language] = t;
    return acc;
  }, {});

  const filteredKeys = Object.keys(groupedTranslations).filter(key =>
    key.toLowerCase().includes(search.toLowerCase())
  );

  const languages = [
    { code: 'sq', name: 'Albanian', flag: '🇦🇱' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-translations">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white">Translations</h1>
          <p className="text-white/50">Manage multilingual content (Albanian, German, English)</p>
        </div>
        <Button 
          onClick={saveAllChanges} 
          disabled={saving}
          className="bg-brand-red hover:bg-brand-red-light"
          data-testid="save-translations-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save All Changes
        </Button>
      </div>

      {/* Search */}
      <Card className="bg-brand-dark border-white/5">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Search by key..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-charcoal border-white/10 text-white"
              data-testid="translation-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Add New Translation */}
      <Card className="bg-brand-dark border-white/5">
        <CardHeader>
          <CardTitle className="font-syne text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-brand-gold" />
            Add New Translation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white/70">Key</Label>
            <Input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="bg-brand-charcoal border-white/10 text-white"
              placeholder="e.g., button.submit"
              data-testid="new-translation-key"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {languages.map(lang => (
              <div key={lang.code} className="space-y-2">
                <Label className="text-white/70 flex items-center gap-2">
                  <span>{lang.flag}</span> {lang.name}
                </Label>
                <Input
                  value={newValues[lang.code]}
                  onChange={(e) => setNewValues(prev => ({ ...prev, [lang.code]: e.target.value }))}
                  className="bg-brand-charcoal border-white/10 text-white"
                  placeholder="Translation..."
                  data-testid={`new-translation-${lang.code}`}
                />
              </div>
            ))}
          </div>
          <Button
            onClick={addNewTranslation}
            disabled={saving || !newKey.trim()}
            className="bg-brand-gold hover:bg-brand-gold-light"
            data-testid="add-translation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Translation
          </Button>
        </CardContent>
      </Card>

      {/* Existing Translations */}
      <Card className="bg-brand-dark border-white/5">
        <CardHeader>
          <CardTitle className="font-syne text-white">Existing Translations</CardTitle>
          <CardDescription className="text-white/50">
            {filteredKeys.length} translation keys found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {filteredKeys.map(key => (
              <div 
                key={key} 
                className="p-4 bg-brand-charcoal rounded-lg border border-white/5"
                data-testid={`translation-${key}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <code className="text-brand-gold text-sm font-mono">{key}</code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      languages.forEach(lang => {
                        if (groupedTranslations[key][lang.code]) {
                          deleteTranslation(lang.code, key);
                        }
                      });
                    }}
                    className="text-white/40 hover:text-brand-red"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {languages.map(lang => {
                    const translation = groupedTranslations[key][lang.code];
                    return (
                      <div key={lang.code} className="space-y-1">
                        <Label className="text-white/50 text-xs flex items-center gap-1">
                          <span>{lang.flag}</span> {lang.name}
                        </Label>
                        <Input
                          value={translation?.value || ''}
                          onChange={(e) => handleValueChange(lang.code, key, e.target.value)}
                          className={`bg-brand-dark border-white/10 text-white text-sm ${
                            translation?.changed ? 'border-brand-gold' : ''
                          }`}
                          placeholder="Not translated"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {filteredKeys.length === 0 && (
              <div className="text-center py-8">
                <p className="text-white/50">No translations found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Translations;
